import React from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from '../utils/toastBus';
import ProjectNameInput from '../components/onboarding/ProjectNameInput';
import ProjectCard from '../components/ProjectCard';
import { resolveMediaUrl } from '../services/api';
import { Button, Skeleton, EmptyState } from '../components/ui';

const STAGE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs-audio', label: 'Needs Voice' },
  { id: 'needs-image', label: 'Needs Visuals' },
  { id: 'needs-video', label: 'Needs Video' },
  { id: 'ready', label: 'Has Video' }
];

export default function ProjectManager() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [name, setName] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [stageFilter, setStageFilter] = React.useState('all');

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.composerListProjects();
      if (res?.ok === false && res?.error) {
        setError(res.error);
        setItems([]);
      } else {
        setItems(Array.isArray(res?.items) ? res.items : []);
      }
    } catch (e) {
      setError('failed');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const derived = React.useMemo(() => {
    const normalizeCounts = (project) => {
      const counts = project?.counts || {};
      return {
        audio: Number(counts.audio || counts.voices || 0),
        images: Number(counts.images || 0),
        videos: Number(counts.videos || 0)
      };
    };

    const stageForProject = (project) => {
      const counts = normalizeCounts(project);
      if (counts.videos > 0) return 'ready';
      if (counts.audio > 0 && counts.images > 0) return 'needs-video';
      if (counts.audio > 0) return 'needs-image';
      return 'needs-audio';
    };

    const stats = items.reduce(
      (acc, project) => {
        const stage = stageForProject(project);
        acc.total += 1;
        acc[stage] += 1;
        return acc;
      },
      { total: 0, 'needs-audio': 0, 'needs-image': 0, 'needs-video': 0, ready: 0 }
    );

    const filtered = items
      .filter((project) => {
        if (stageFilter === 'all') return true;
        return stageForProject(project) === stageFilter;
      })
      .filter((project) => {
        if (!search.trim()) return true;
        const haystack = `${project?.name || ''} ${project?.context || ''}`.toLowerCase();
        return haystack.includes(search.trim().toLowerCase());
      });

    return { filtered, stats };
  }, [items, search, stageFilter]);

  const latestChip = (project) => {
    try {
      const lat = project?.latestAssets || {};
      const thumb = lat.imageThumb ? resolveMediaUrl(lat.imageThumb) : null;
      const audioName = lat.audioUrl ? decodeURIComponent(String(lat.audioUrl).split('/').pop() || '') : null;
      return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {audioName ? (
            <span style={{ background: '#1f2937', color: '#e5e7eb', borderRadius: 999, padding: '4px 8px', fontSize: 12 }}>
              🎧 {audioName}
            </span>
          ) : null}
          {thumb ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1f2937', color: '#e5e7eb', borderRadius: 999, padding: '4px 8px', fontSize: 12 }}>
              🖼️ Image ready
            </span>
          ) : null}
          {lat.videoUrl ? (
            <span style={{ background: '#1f2937', color: '#e5e7eb', borderRadius: 999, padding: '4px 8px', fontSize: 12 }}>
              🎬 Video ready
            </span>
          ) : null}
        </div>
      );
    } catch {
      return null;
    }
  };

  return (
    <div style={{ padding: 16, display: 'grid', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: '#e5e7eb', margin: '8px 0 4px 0', fontSize: 28, fontWeight: 800 }}>
            Projects
          </h1>
          <p style={{ color: '#9ca3af', margin: 0, fontSize: 14 }}>
            Manage campaigns, track progress, and launch new creatives.
          </p>
        </div>
        <Link
          to="/quickstart"
          style={{
            background: '#2563eb',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: 10,
            padding: '10px 16px',
            fontWeight: 600
          }}
        >
          Quick Start Wizard →
        </Link>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12
        }}
      >
        <SummaryCard label="Total Projects" value={derived.stats.total} />
        <SummaryCard label="Ready to Publish" value={derived.stats.ready} accent="#10b981" />
        <SummaryCard label="Needs Video" value={derived.stats['needs-video']} accent="#f59e0b" />
        <SummaryCard label="Needs Voice" value={derived.stats['needs-audio']} accent="#f97316" />
      </div>

      <div
        style={{
          background: '#0f1115',
          border: '1px solid #1f2937',
          borderRadius: 12,
          padding: 16,
          display: 'grid',
          gap: 12
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <ProjectNameInput value={name} onChange={setName} placeholder="New project name" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              disabled={!String(name).trim()}
              onClick={async () => {
                try {
                  const res = await api.composerCreateProject({ name });
                  if (res?.error) {
                    try {
                      toast({ type: 'error', message: `Create failed: ${res.error}` });
                    } catch {}
                  } else {
                    try {
                      toast({ type: 'success', message: 'Project created' });
                    } catch {}
                    setName('');
                    await load();
                  }
                } catch (e) {
                  try {
                    toast({ type: 'error', message: 'Create failed' });
                  } catch {}
                }
              }}
            >
              Create Project
            </Button>
            <Button variant="secondary" onClick={load}>
              Refresh
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or context…"
            style={{
              flex: '1 1 220px',
              minWidth: 200,
              background: '#0b0f17',
              color: '#e5e7eb',
              border: '1px solid #1f2937',
              borderRadius: 8,
              padding: '10px 12px'
            }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {STAGE_FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setStageFilter(filter.id)}
                style={{
                  background: stageFilter === filter.id ? '#2563eb' : '#1f2937',
                  color: stageFilter === filter.id ? '#fff' : '#d1d5db',
                  border: 0,
                  borderRadius: 999,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <Skeleton variant="title" width="40%" />
          <Skeleton variant="text" width="60%" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="card" height="220px" />
            ))}
          </div>
        </div>
      ) : error ? (
        <EmptyState
          icon="⚠️"
          title="Failed to load projects"
          description={String(error || 'Unknown error')}
          actionText="Retry"
          onAction={load}
          variant="error"
        />
      ) : derived.filtered.length === 0 ? (
        <EmptyState
          icon="📁"
          title="No projects found"
          description="Try adjusting filters or start a new project with Quick Start."
          actionText="Launch Quick Start"
          onAction={() => { window.location.href = '/quickstart'; }}
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 14
          }}
        >
          {derived.filtered.map((project) => (
            <div key={project.id} style={{ display: 'grid', gap: 6 }}>
              <ProjectCard project={project} onChanged={load} />
              {latestChip(project)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent = '#ff5a1f' }) {
  return (
    <div
      style={{
        background: '#0b0f17',
        border: '1px solid #1f2937',
        borderRadius: 12,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 6
      }}
    >
      <span style={{ color: '#9ca3af', fontSize: 13 }}>{label}</span>
      <span style={{ color: accent, fontSize: 26, fontWeight: 700 }}>{value}</span>
    </div>
  );
}
